import { createRef, useState } from "react";
import styled from 'styled-components/macro';
import classNames from 'classnames';
import { PinchPanImg, PinchPan } from "./utils/pinch-pan-helpers";
import HotspotFlag from "./ui/hotspot-flag";
import ProductPin from "./ui/product-pin";
import { getOffsetPosition } from "./utils/position";
import { useStore } from "./store";
import { useHistory, useParams } from "react-router-dom";
import { HotspotList, HotspotConfig } from "../config/city";
import { CSSTransition } from "react-transition-group";
import { BuildingType } from "../config/buildings";
import BuildingNavigation from "./ui/building-navigation";
import { RouteParams } from "../interfaces/router";
import { BuildingTechSectors } from "../interfaces/buildings";
import { products, ProductType } from "../config/products";
import useTranslations from './utils/language-provider';
import Breadcrumbs, { BreadcrumbsWrapper, ButtonsWrapper, StyledContactButton } from "./ui/breadcrumbs";
import FullscreenToggleButton from "./ui/fullscreen-button";
import { isMobileDevice } from "./utils/mobile-detection";
import { ReactComponent as Contact } from '../assets/icon-contact.svg';

type BuildingProducts = {
    type: ProductType;
    position: {x: number, y: number}
}

type BuildingsAndProductsProps = {
    visible: boolean;
    pageTitle?: string;
    imgRef?: React.RefObject<HTMLImageElement>;
    onImgLoad?(): void;
    src: string;
    hotspots?: HotspotList;
    techSectors?: BuildingTechSectors[];
    transitionClassName?: string;
    type?: BuildingType
}

const StyledHotspotFlag = styled(HotspotFlag)`
    --animate-duration: 700ms;
    opacity: 0;

    &.active {
        opacity: 1;
    }
`;

const StyledProductPin = styled(ProductPin)`
    position: absolute;
    --animate-duration: 700ms;
    opacity: 0;

    &.active {
        opacity: 1;
    }
`;

const ZoomContainer = styled.div`
    position: relative;
    overflow: visible;
`

const BuildingsAndProducts = ({
    visible,
    imgRef,
    src,
    pageTitle,
    hotspots,
    techSectors,
    transitionClassName = 'page-zoom',
    onImgLoad = () => {},
    type
}: BuildingsAndProductsProps) => {
    const {state} = useStore();
    const history = useHistory();
    const translations = useTranslations();
    const [hotspotsActive, setHotspotsActive] = useState(false);
    const containerRef = createRef<HTMLDivElement>();
    const {techSector: techSectorParam = '', product: productParam} = useParams<RouteParams>();
    const hasSubbuildings = techSectors?.length;
    let spots = hotspots;
    let img = src;
    let productsConfig: BuildingProducts[] = [];

    if (hasSubbuildings) {
        const sectorDetails = techSectors?.find(sector => sector.type === techSectorParam);

        if (sectorDetails) {
            spots = sectorDetails.buildings;
            img = sectorDetails.img || '';
            productsConfig = sectorDetails.products || [];
        }
    }

    const handleImgLoad = () => {
        onImgLoad();
        setHotspotsActive(true);
    }

    const openBuilding = (path: string, coords: string) => {
        if (containerRef.current) {
            containerRef.current.style.transformOrigin = coords
        }
        history.push(`/${path}`);
    }

    const handleComeback = () => {
        setHotspotsActive(true);
    }

    const handleHotspotClick = (config: HotspotConfig, pos: {left: string, top: string}) => {
        if (config.type !== 'inactive') {
            openBuilding(config.link || config.building, `${pos.left} ${pos.top}`)
        }
    }
    return (
        <div>
            <ZoomContainer>
                {type && (
                    <>
                        <BreadcrumbsWrapper>
                            <Breadcrumbs
                                building={{
                                    label: translations.buildings[type].name,
                                    href: `/${type}`,
                                }}
                                subBuilding={undefined}
                                techSektor={
                                    techSectorParam
                                        ? {
                                              label: translations.sectors[techSectorParam].name,
                                              href: `/${type}/${techSectorParam}`,
                                          }
                                        : undefined
                                }
                            />
                            <ButtonsWrapper>
                                {isMobileDevice() && <FullscreenToggleButton />}
                                <StyledContactButton href={translations.contact?.href || '/contact'}>
                                    <Contact />
                                    <p>{translations.contact?.text || 'Kontakt'}</p>
                                </StyledContactButton>
                            </ButtonsWrapper>
                        </BreadcrumbsWrapper>
                        {hasSubbuildings && <BuildingNavigation buildingType={type} />}
                    </>
                )}

                <CSSTransition
                    in={visible}
                    timeout={500}
                    classNames={transitionClassName}
                    nodeRef={containerRef}
                    onEntered={() => handleComeback()}
                    onExited={() => setHotspotsActive(false)}
                >
                    <div ref={containerRef}>
                        <PinchPan layer={2}>
                            <PinchPanImg forwardedRef={imgRef} src={img} onLoad={handleImgLoad} />

                            {hotspotsActive &&
                                spots?.map((hotspotConfig, index) => {
                                    const pos = getOffsetPosition({
                                        ...hotspotConfig.coordinates,
                                        imgWidth: state.appDimensions.width,
                                        imgHeight: state.appDimensions.height,
                                    });

                                    if (hotspotConfig.component) {
                                        return (
                                            <hotspotConfig.component
                                                key={hotspotConfig.building}
                                                style={{
                                                    ...pos,
                                                    animationDelay: `${(index + 0.3) * 100}ms`,
                                                }}
                                                className={classNames('animated', {
                                                    fadeIn: hotspotsActive,
                                                    active: hotspotsActive,
                                                })}
                                            />
                                        );
                                    }

                                    return (
                                        <StyledHotspotFlag
                                            config={hotspotConfig}
                                            key={hotspotConfig.building}
                                            style={{
                                                ...pos,
                                                animationDelay: `${index * 100}ms`,
                                            }}
                                            className={classNames('animated', {
                                                fadeIn: hotspotsActive,
                                                active: hotspotsActive,
                                            })}
                                            onClickTooltip={() => handleHotspotClick(hotspotConfig, pos)}
                                        />
                                    );
                                })}

                            {hotspotsActive &&
                                productsConfig?.map((product, index) => (
                                    <StyledProductPin
                                        popoverOpenDefault={productParam === product.type}
                                        key={`${product.type}${index}`}
                                        productType={product.type}
                                        showNewFlag={products[product.type]?.new}
                                        className={classNames('animated', {
                                            fadeIn: hotspotsActive,
                                            active: hotspotsActive,
                                        })}
                                        coordinates={product.position}
                                        style={{
                                            ...getOffsetPosition({
                                                x: product.position.x,
                                                y: product.position.y,
                                                imgWidth: state.appDimensions.width,
                                                imgHeight: state.appDimensions.height,
                                                imgOffsetX: isMobileDevice() ? 10 : 0,
                                                imgOffsetY: isMobileDevice() ? 10 : 0,
                                                imgScale: 1,
                                            }),
                                            animationDelay: `${(spots?.length || 0) + index * 100}ms`,
                                        }}
                                    />
                                ))}
                        </PinchPan>
                    </div>
                </CSSTransition>
            </ZoomContainer>
        </div>
    );
}

export default BuildingsAndProducts;
